import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import i18next from "i18next";
import { API_ROOT } from "../../app/helpers/conexion-urls";
import { enabledLanguageOptions } from "../../app/helpers/uiLanguagesHelper";
import {
  userStartLoadOwnProfile,
  userStartRequestEmailChange,
  userStartRequestPasswordChange,
  userStartSavePreferences,
  userStartUploadAvatar,
} from "../../app/actions/userActions";
import LanguageFlag from "../generic-component/LanguageFlag";
import EditUserPreferences from "./EditUserPreferences";

const AccountPreferences = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { userPreferences, userSelectedForPreferences } = useSelector((state) => state.user);
  const profileReady = String(userSelectedForPreferences?.id || "") === String(user?.user_id || "");
  const configuration = useSelector((state) => state.configuration);
  const themeEnabled = String(configuration?.uiThemeEnabled) === "true";
  const languageOptions = enabledLanguageOptions(configuration?.uiLanguagesEnabled);
  const [section, setSection] = useState("profile");
  const [email, setEmail] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    if (user?.user_id) {
      dispatch(userStartLoadOwnProfile(user.user_id));
    }
  }, [dispatch, user?.user_id]);

  const sections = [
    { id: "profile", label: t("accountPreferences.profile") },
    { id: "password", label: t("accountPreferences.password") },
    { id: "language", label: t("accountPreferences.language") },
    { id: "avatar", label: t("accountPreferences.avatar") },
    { id: "email", label: t("accountPreferences.email") },
  ];
  if (themeEnabled) {
    sections.splice(1, 0, { id: "theme", label: t("accountPreferences.theme") });
  }

  const savePreference = async (partial) => {
    const body = await dispatch(userStartSavePreferences(partial));
    if (body && body.ok) setNotice(t("accountPreferences.saved"));
  };

  const sendPasswordMail = async () => {
    setNotice("");
    const body = await dispatch(userStartRequestPasswordChange());
    if (body && body.ok) {
      setNotice(t("accountPreferences.passwordSent", { validity: body.validity }));
    }
  };

  const sendEmailMail = async () => {
    setNotice("");
    const body = await dispatch(userStartRequestEmailChange(email));
    if (body && body.ok) {
      setNotice(t("accountPreferences.emailSent", { validity: body.validity }));
      setEmail("");
    }
  };

  const onAvatar = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    setNotice("");
    const body = await dispatch(userStartUploadAvatar(file));
    if (body && body.ok) setNotice(t("accountPreferences.avatarSaved"));
    event.target.value = "";
  };

  const avatarUrl = userPreferences?.avatar_path
    ? `${API_ROOT}/file-uploads/${userPreferences.avatar_path}`
    : "";

  return (
    <div style={{ display: "flex", gap: "1.5rem", alignItems: "flex-start", padding: "1.5rem 2rem" }}>
      <aside style={{ width: 220, flexShrink: 0 }}>
        <div className="page-title" style={{ fontSize: "1.1rem", marginBottom: "0.8rem" }}>
          {t("accountPreferences.title")}
        </div>
        {sections.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => { setSection(item.id); setNotice(""); }}
            className="account-preferences-nav-btn"
            data-active={section === item.id ? "true" : "false"}
            style={{
              display: "block",
              width: "100%",
              textAlign: "left",
              marginBottom: 6,
              padding: "0.55rem 0.75rem",
              border: "1px solid #d5dde6",
              borderRadius: 6,
              background: section === item.id ? "#e7f6ee" : "#fff",
              color: "#1d3b2a",
              fontWeight: section === item.id ? 700 : 500,
            }}
          >
            {item.label}
          </button>
        ))}
      </aside>
      <section className="account-preferences-panel" style={{ flex: 1, minWidth: 0 }}>
        {notice && (
          <div style={{ background: "#fff8e8", color: "#3d3420", border: "1px solid #f0d78c", borderRadius: 8, padding: "10px 14px", marginBottom: 16 }}>
            {notice}
          </div>
        )}
        {section === "profile" && (
          <div>
            <p>{t("accountPreferences.profileHelp")}</p>
            {profileReady ? <EditUserPreferences embedded /> : null}
          </div>
        )}
        {section === "theme" && themeEnabled && (
          <div>
            <p>{t("accountPreferences.themeHelp")}</p>
            {["light", "dark", "auto"].map((value) => (
              <label key={value} style={{ display: "block", marginBottom: 8 }}>
                <input
                  type="radio"
                  name="theme"
                  checked={(userPreferences?.theme || "light") === value}
                  onChange={() => savePreference({ theme: value })}
                />{" "}
                {t(`accountPreferences.themeModes.${value}`)}
              </label>
            ))}
          </div>
        )}
        {section === "password" && (
          <div>
            <p>{t("accountPreferences.passwordHelp")}</p>
            <button type="button" className="btn btn-primary" onClick={sendPasswordMail}>
              {t("accountPreferences.passwordSend")}
            </button>
          </div>
        )}
        {section === "language" && (
          <div>
            <p>{t("accountPreferences.languageHelp")}</p>
            {languageOptions.map((option) => (
              <label key={option.code} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <input
                  type="radio"
                  name="language"
                  checked={(i18next.resolvedLanguage || i18next.language || "").slice(0, 2) === option.code}
                  onChange={() => {
                    i18next.changeLanguage(option.code);
                    savePreference({ language: option.code });
                  }}
                />
                <LanguageFlag code={option.code} size={18} />
                {option.label}
              </label>
            ))}
          </div>
        )}
        {section === "avatar" && (
          <div>
            <p>{t("accountPreferences.avatarHelp")}</p>
            {avatarUrl && (
              <img src={avatarUrl} alt="" style={{ width: 96, height: 96, objectFit: "cover", borderRadius: "50%", marginBottom: 12 }} />
            )}
            <input type="file" accept="image/png,image/jpeg,image/webp" onChange={onAvatar} />
          </div>
        )}
        {section === "email" && (
          <div>
            <p>{t("accountPreferences.emailHelp")}</p>
            <p><strong>{t("accountPreferences.currentEmail")}:</strong> {userSelectedForPreferences?.email || ""}</p>
            <input
              className="form-control"
              style={{ maxWidth: 360, marginBottom: 12 }}
              type="email"
              value={email}
              placeholder={t("accountPreferences.emailPlaceholder")}
              onChange={(event) => setEmail(event.target.value)}
            />
            <button type="button" className="btn btn-primary" onClick={sendEmailMail}>
              {t("accountPreferences.emailSend")}
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

export default AccountPreferences;
